"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { BrandLogo } from "@/app/components/atoms/BrandLogo";
import { IconProfile, IconProfileActive } from "@/app/components/atoms/Icons";
import { Toast } from "@/app/components/atoms/Toast";
import { LoadingOverlay } from "@/app/components/shared/LoadingOverlay";
import {
  SearchInput,
  type SearchProduct,
  type TopSearch,
} from "@/app/components/shared/search/SearchInput";
import { useSearchContext } from "@/app/context/search";
import { useCityContext } from "@/app/context/city";
import { useSessionContext } from "@/app/context/session";
import { useAppPromotionCookie } from "@/app/lib/hooks/useAppPromotionCookie";
import { isHomePathname, isPerformancePathname, route } from "@/app/lib/routing";
import { cx, lockDocumentScroll } from "@/app/lib/utils";
import { SEARCH_BAND_ANCHOR_ID } from "@/app/lib/constants";
import { AccountDrawer } from "./AccountDrawer";
import { HamburgerMenu } from "./HamburgerMenu";
import { toNavCategories, type MenuCategory } from "./NavDrawer";
import { signInAction, signOutAction } from "./authActions";
import {
  ACCOUNT_ACTION_ERROR_MESSAGE,
  ACCOUNT_ACTION_LOADING_LABEL,
} from "./constants";
import type { AccountActionState } from "./types";
import styles from "./Header.module.scss";

// Diferido fuera del bundle inicial: el drawer no es contenido que el
// visitante vea de inmediato en ninguna página, solo tras pulsar el icono de
// menú — mismo principio de carga on-demand en la primera interacción que la
// constitución ya exige para widgets de terceros (Principio X), aplicado aquí
// a un componente propio (ver specs/010-mejora-rendimiento-web/research.md §5).
const NavDrawer = dynamic(
  () => import("./NavDrawer/NavDrawer").then((m) => m.NavDrawer),
  {
    ssr: false,
  },
);

const SEARCH_PLACEHOLDER = "Busca por evento, ubicación o fecha";

/** URL de retorno para el login/logout: la actual, o la home si no se puede determinar (FR-008). */
function getAccountReturnUrl(): string {
  return window.location.href || route("/");
}

/** Altura (px) del logotipo de la cabecera. */
const LOGO_HEIGHT = 40;

export interface HeaderProps {
  /** `menu` de la respuesta del layout → categorías del drawer. */
  menu?: MenuCategory[];
  /** `module_search.top_search` del layout. */
  topSearches?: TopSearch[];
  /** `module_search.products_most_searched` del layout. */
  products?: SearchProduct[];
  /** Variante visual. `transparent` para uso sobre heros de página. */
  variant?: "default" | "transparent";
  /** Muestra el crespón a la derecha del logo. */
  showCrespon?: boolean;
}

/**
 * Cabecera del sitio (Server-driven data, cliente). Header sticky
 * (logo, buscador, menú, cuenta) + `NavDrawer`. El estado de apertura del menú
 * vive en `SearchContext`; el de sesión (círculo verde del icono de cuenta),
 * en `SessionContext`. Fondo negro DS siempre; el buscador propio del header
 * solo se muestra en páginas interiores, o en home al pasar la SearchBand o
 * con el menú abierto.
 *
 * Modo app (cookie `eciAppVersion=eci-app-focus`, `useAppPromotionCookie`,
 * Figma node 5908:34677): la web se abre embebida en la app nativa, así que
 * logo, buscador integrado y el icono de cuenta no aplican — quedan el menú
 * hamburguesa y el icono de lupa (mismo lanzador de búsqueda que ya usa
 * la ficha de evento, forzado aquí igual que `isPerformancePage`).
 */
export function Header({
  menu = [],
  topSearches = [],
  products = [],
  variant = "default",
  showCrespon = false,
}: HeaderProps) {
  const { location } = useCityContext();
  const { isLoggedIn, userName } = useSessionContext();
  const categories = useMemo(
    () => toNavCategories(menu, location),
    [menu, location],
  );
  const pathname = usePathname();
  // Home general (`/entradas/`) o localizada (`/entradas/{provincia}/`, por
  // rewrite sin cambiar la URL): ambas llevan SearchBand propia.
  const isHome = isHomePathname(pathname);
  // La ficha de evento sí ofrece buscador, pero en móvil/tablet no como fila
  // propia del header: se lanza desde el icono de lupa de la barra de
  // navegación (diseño PDP; sustituye a FR-052 de specs/043). Se detecta por la
  // forma de la ruta, que `usePathname` resuelve de forma síncrona: propagar el
  // `type` de la respuesta hasta aquí obligaría a atravesar un layout
  // compartido por todo el sitio para el mismo resultado.
  const isPerformancePage = isPerformancePathname(pathname);
  const isAppContext = useAppPromotionCookie();

  const [scrolledPast, setScrolledPast] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  const { menuOpen, setMenuOpen } = useSearchContext();

  // Monta NavDrawer solo a partir del primer clic (nunca antes, y puede venir
  // de `Header` o de `NavbarSearch` — ambos comparten `menuOpen` vía
  // `SearchContext`) — su chunk diferido no debe cargarse hasta que haga
  // falta, pero una vez montado se queda así (no se desmonta al cerrar) para
  // no cortar su animación de cierre. Ajuste de estado durante el render
  // (patrón "Adjusting some state when a prop changes" de React), no en un
  // efecto: no hay ningún sistema externo que sincronizar, solo derivar un
  // valor que debe quedar fijado la primera vez que `menuOpen` es `true`.
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  if (menuOpen && !hasOpenedOnce) {
    setHasOpenedOnce(true);
  }

  // El buscador propio del header solo se muestra en páginas interiores
  // (siempre) o en home una vez se ha hecho scroll más allá de la SearchBand,
  // o mientras el menú está abierto. En home arranca oculto — sin flash,
  // porque `isHome` se resuelve de forma síncrona por pathname, no por DOM.
  const integrated = !isHome || scrolledPast || menuOpen;

  // La fila de búsqueda de móvil/tablet (la que hace crecer el header) se
  // omite en la ficha de evento: ahí la búsqueda se abre desde el icono de
  // lupa del nav row, que no ocupa alto extra. En desktop no cambia nada — el
  // buscador vive en el centro del nav row en todas las páginas interiores.
  const searchRowVisible = integrated && !isPerformancePage && !isAppContext;

  // En home: comprueba en cada scroll si la SearchBand ha quedado por encima
  // del header. Un listener de scroll (no un IntersectionObserver) porque el
  // cruce de intersección solo dispara una vez al entrar/salir de la zona
  // observada — al volver a subir, el valor se queda «pegado» al de ese
  // instante y dejaba de actualizarse en mitad del recorrido. Recalcular en
  // cada evento de scroll es continuo y funciona en ambas direcciones.
  //
  // El ancla se busca en cada comprobación, no una vez al montar: al pasar de
  // la home general a la localizada (la redirección de la geolocalización) el
  // header no se remonta y `isHome` no cambia, pero la SearchBand sí es otra.
  // Un ancla guardada quedaría desconectada del DOM (rect a 0 ⇒ «ya pasada»
  // siempre) y el buscador del header no volvía a ocultarse al subir.
  useEffect(() => {
    if (!isHome) return;

    const check = () => {
      const anchor = document.getElementById(SEARCH_BAND_ANCHOR_ID);
      if (!anchor) return;
      const headerHeight =
        headerRef.current?.getBoundingClientRect().height ?? 0;
      setScrolledPast(anchor.getBoundingClientRect().top <= headerHeight);
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [isHome, pathname]);

  // Bloquea el scroll de la página mientras el menú está abierto.
  useEffect(() => {
    if (!menuOpen) return;
    return lockDocumentScroll();
  }, [menuOpen]);

  // Al cerrar el menú (Escape, clic fuera, un enlace), el foco que estaba
  // dentro se pierde al desmontarse el panel: vuelve a la «X», que es su
  // botón (WCAG 2.4.3). Solo si se ha perdido, para no robárselo a quien ya
  // lo haya movido a otro sitio (p. ej. el buscador).
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasMenuOpenRef.current;
    wasMenuOpenRef.current = menuOpen;
    if (!wasOpen || menuOpen) return;
    const active = document.activeElement;
    if (!active || active === document.body) {
      hamburgerRef.current?.focus({ preventScroll: true });
    }
  }, [menuOpen]);

  // Publica el borde inferior real del header en `--header-bottom`: cualquier
  // elemento sticky de página (p. ej. el índice alfabético de Artistas/
  // Recintos) y los paneles del `NavDrawer` se anclan ahí para colocarse justo
  // debajo del header con su alto real, que varía con el
  // buscador integrado en móvil/tablet.
  //
  // Es la ÚNICA medición del header: el drawer la consume desde CSS
  // (`padding-top: var(--header-bottom)`) en vez de recibirla como prop, así
  // que no hay estado de React —ni re-render— por medio y el panel nunca se
  // pinta sin desplazamiento.
  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        "--header-bottom",
        `${header.getBoundingClientRect().bottom}px`,
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);
    window.addEventListener("resize", publish);
    header.addEventListener("transitionend", publish);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
      header.removeEventListener("transitionend", publish);
    };
  }, [integrated, searchRowVisible]);

  const handleMenuToggle = useCallback(
    () => setMenuOpen(!menuOpen),
    [menuOpen, setMenuOpen],
  );
  const handleMenuClose = useCallback(() => setMenuOpen(false), [setMenuOpen]);

  // Al cerrar el overlay de búsqueda, cierra también el menú de categorías si
  // estaba abierto: el buscador del header puede abrirse sobre el menú (con el
  // menú abierto, `integrated` deja visible el buscador), y cerrarlo debe
  // devolver al usuario a la página, no dejar el menú abierto debajo.
  // `setMenuOpen(false)` es no-op si el menú ya estaba cerrado, así que el
  // flujo normal del buscador no se ve afectado.
  const handleSearchOpenChange = useCallback(
    (searchOpen: boolean) => {
      if (!searchOpen) setMenuOpen(false);
    },
    [setMenuOpen],
  );

  // Guarda anti-doble-clic (FR-007): un `ref`, no estado, porque debe bloquear
  // clics síncronos que llegan antes de que React re-renderice con el nuevo
  // estado `pending`.
  const accountActionPendingRef = useRef(false);
  const [accountAction, setAccountAction] = useState<AccountActionState>({
    status: "idle",
    errorMessage: null,
  });

  // Login/logout: llama al servicio de auth (server action) con la URL actual
  // como retorno (o la home si no se puede determinar — FR-008) y redirige a
  // la URL (OAuth) que devuelve. En error, muestra un aviso visible (FR-006)
  // sin tocar el estado de sesión mostrado.
  const runAccountAction = useCallback(
    async (action: typeof signInAction | typeof signOutAction) => {
      if (accountActionPendingRef.current) return;
      accountActionPendingRef.current = true;
      setAccountAction({ status: "pending", errorMessage: null });

      const { redirectUrl } = await action(getAccountReturnUrl());

      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      accountActionPendingRef.current = false;
      setAccountAction({
        status: "error",
        errorMessage: ACCOUNT_ACTION_ERROR_MESSAGE,
      });
    },
    [],
  );

  // Si el usuario vuelve atrás desde la página de login, el navegador puede
  // restaurar esta página desde la bfcache tal cual se quedó (con el spinner
  // de la redirección): se resetea para que no se quede bloqueada.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      accountActionPendingRef.current = false;
      setAccountAction({ status: "idle", errorMessage: null });
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Cada acción sale de su propio botón del panel «Accesos» (CTA de login sin
  // sesión, «Cerrar sesión» con sesión), sin decidir por `isLoggedIn` en el
  // momento del click: el icono de cuenta solo abre el panel.
  const handleSignIn = useCallback(
    () => runAccountAction(signInAction),
    [runAccountAction],
  );
  const handleSignOut = useCallback(
    () => runAccountAction(signOutAction),
    [runAccountAction],
  );

  return (
    <>
      <header
        ref={headerRef}
        className={cx(
          styles.header,
          variant === "transparent" && styles.transparent,
        )}
        data-integrated={integrated ? "" : undefined}
        data-search-row={searchRowVisible ? "" : undefined}
        data-instant={!isHome ? "" : undefined}
        data-app-context={isAppContext ? "" : undefined}
      >
        <div className={styles.bg} aria-hidden="true" />

        <div className={styles.inner}>
          <div className={styles.start}>
            <HamburgerMenu
              ref={hamburgerRef}
              open={menuOpen}
              onToggle={handleMenuToggle}
            />

            {!isAppContext && (
              <Link
                href={route("/")}
                className={styles.brand}
                aria-label="El Corte Inglés — Inicio"
              >
                <BrandLogo height={LOGO_HEIGHT} showCrespon={showCrespon} />
              </Link>
            )}
          </div>

          {!isAppContext && (
            <div className={styles.center}>
              <div
                className={styles.searchField}
                data-visible={integrated ? "" : undefined}
                inert={!integrated}
              >
                <SearchInput
                  variant="full"
                  bandStyle={false}
                  inverse
                  placeholder={SEARCH_PLACEHOLDER}
                  topSearches={topSearches}
                  products={products}
                  onOpenChange={handleSearchOpenChange}
                />
              </div>
            </div>
          )}

          <div className={styles.end}>
            {(isPerformancePage || isAppContext) && (
              <div className={styles.searchIcon}>
                <SearchInput
                  variant="icon"
                  placeholder={SEARCH_PLACEHOLDER}
                  topSearches={topSearches}
                  products={products}
                  onOpenChange={handleSearchOpenChange}
                />
              </div>
            )}

            {!isAppContext && !menuOpen && (
              <div className={styles.accountWrapper}>
                {/* El icono abre el panel «Accesos» (`AccountDrawer`), con o
                    sin sesión. Busto de elcorteingles.es: solo trazo sin
                    sesión y relleno con sesión; con sesión y nombre, el
                    nombre al lado. El estado no depende solo del icono: el
                    nombre accesible del botón también lo dice. */}
                <AccountDrawer
                  isLoggedIn={isLoggedIn}
                  userName={userName}
                  onSignIn={handleSignIn}
                  onSignOut={handleSignOut}
                  triggerClassName={styles.accountBtn}
                  triggerLabel={
                    isLoggedIn && userName ? `${userName}: mi cuenta` : undefined
                  }
                >
                  <span className={styles.accountIcon}>
                    {isLoggedIn ? (
                      <IconProfileActive size={24} />
                    ) : (
                      <IconProfile size={24} />
                    )}
                  </span>
                  {isLoggedIn && userName && (
                    <span className={styles.accountGreeting}>{userName}</span>
                  )}
                </AccountDrawer>
                {accountAction.status === "error" &&
                  accountAction.errorMessage && (
                    <Toast
                      variant="error"
                      message={accountAction.errorMessage}
                      closable
                      onClose={() =>
                        setAccountAction({ status: "idle", errorMessage: null })
                      }
                      className={styles.accountError}
                    />
                  )}
              </div>
            )}
          </div>
        </div>

        {/* En la ficha de evento y en modo app la fila no se monta (no solo
            se oculta): su caja de 68px seguiría desbordando por debajo del
            header y el buscador que aloja sería un tercer lanzador inerte.
            El resto de páginas la mantienen montada siempre para poder
            animar su entrada/salida en home. */}
        {!isPerformancePage && !isAppContext && (
          <div
            className={styles.searchRow}
            data-visible={searchRowVisible ? "" : undefined}
            inert={!searchRowVisible}
          >
            <div className={styles.searchRowInner}>
              <SearchInput
                variant="full"
                bandStyle={false}
                inverse
                placeholder={SEARCH_PLACEHOLDER}
                topSearches={topSearches}
                products={products}
                onOpenChange={handleSearchOpenChange}
              />
            </div>
          </div>
        )}
      </header>

      {/* Spinner mientras se resuelve el login/logout y hasta que el
          navegador sale hacia la URL de la cuenta. */}
      {accountAction.status === "pending" && (
        <LoadingOverlay label={ACCOUNT_ACTION_LOADING_LABEL} />
      )}

      {hasOpenedOnce && (
        <NavDrawer
          open={menuOpen}
          onClose={handleMenuClose}
          categories={categories}
        />
      )}
    </>
  );
}
